import { DialOptionType } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-path/v1/api.pb';
import dayjs from 'dayjs';
import { PhoneAnalyticsTypes } from '@frontend/api-analytics';
import { demoDataUtils } from '@frontend/analytics-shared';
import type { CallAnswerRateResponse } from '../hooks/use-fetch-aggregated-call-answer-rate';
import type { TotalCallsAndDurationResponse } from '../hooks/use-fetch-aggregated-calls-and-duration-report';
import type { CallsSummaryResponse } from '../hooks/use-fetch-aggregated-calls-summary';
import type { HourlyInsightsResponse } from '../hooks/use-fetch-aggregated-hourly-insights';
import type { LongDurationCallsResponse } from '../hooks/use-fetch-aggregated-long-duration-call-reports';
import type { DeviceExtensionsResponse } from '../hooks/use-fetch-device-extensions-report';

const totalCallsKeys: PhoneAnalyticsTypes.CallsAggregationKey[] = ['inbound', 'outbound'];
const incomingCallsKeys: PhoneAnalyticsTypes.IncomingCallsAggregationKey[] = ['abandoned', 'answered', 'missed'];
const sourceTypeKeys: PhoneAnalyticsTypes.SourceTypeAggregationKey[] = ['current_patient', 'unknown'];
const hourlyInsightsKey: PhoneAnalyticsTypes.HourlyInsightsAggregationKey[] = [
  'total_calls',
  'total_inbound_calls',
  'total_incoming_abandoned_calls',
  'total_incoming_answered_calls',
  'total_missed_incoming_calls_closed',
  'total_missed_incoming_calls_open',
  'total_missed_incoming_calls',
  'total_outbound_abandoned_calls',
  'total_outbound_answered_calls',
  'total_outbound_calls',
];

const last14Days = demoDataUtils.generateConsecutiveDates(
  new Date(dayjs().subtract(14, 'days').format('YYYY/MM/DD')),
  14
);

const zeroArray = new Array(4).fill(0);
const prefillZeros = (data: number[]) => [...zeroArray, ...data, ...zeroArray];
const generateDailyCount = () => Math.floor(Math.random() * 101);
const generateHourlyCount = () => prefillZeros(Array.from({ length: 16 }, () => Math.floor(Math.random() * 21)));
const generateDailyDuration = () => Math.floor(Math.random() * 500 + 500);
const generateHourlyDuration = () => prefillZeros(Array.from({ length: 16 }, () => Math.floor(Math.random() * 500)));
const extensions = demoDataUtils.generateRandomUserNames(5);

const generateExtensionsCalls = (
  dates: ReturnType<typeof demoDataUtils.generateConsecutiveDates>,
  extensions: ReturnType<typeof demoDataUtils.generateRandomUserNames>
) => {
  const blankArray = new Array(16).fill(0);
  return extensions.map((extension) => {
    return {
      Aggregates: dates.map((date) => {
        // Keep some values absolute zero so charts look more realistic
        const data = Math.random() > 0.5 ? blankArray : blankArray.map(() => Math.floor(Math.random() * 5));

        return {
          Date: dayjs(date).format('YYYY-MM-DD'),
          Values: prefillZeros(data),
        };
      }),
      Extention: `Dr. ${extension.firstName} ${extension.lastName}`,
    };
  });
};

const generateCallsAndDurations = <T extends string>(locationIds: string[], keys: T[]) => ({
  locationIds: locationIds.map((locationId) => ({
    id: locationId,
    dates: last14Days.map((date) => ({
      date: dayjs(date).format('YYYY-MM-DD'),
      aggregates: keys.map((key) => ({
        key,
        values: {
          calls: {
            daily: generateDailyCount(),
            hourly: generateHourlyCount(),
          },
          duration: {
            daily: generateDailyDuration(),
            hourly: generateHourlyDuration(),
          },
        },
      })),
    })),
  })),
});

export const data = (locationIds: string[]) => {
  const callAnswerRate: CallAnswerRateResponse = {
    locationIds: locationIds.map((locationId) => ({
      id: locationId,
      dates: last14Days.map((date) => ({
        date: dayjs(date).format('YYYY-MM-DD'),
        daily: generateDailyCount(),
        hourly: generateHourlyCount(),
      })),
      rate: Math.floor(Math.random() * 100),
    })),
    weave_average: last14Days.map(() => generateDailyCount()),
  };

  const totalCallsAndDurations: TotalCallsAndDurationResponse<PhoneAnalyticsTypes.CallsAggregationKey> =
    generateCallsAndDurations<PhoneAnalyticsTypes.CallsAggregationKey>(locationIds, totalCallsKeys);

  const incomingCallsAndDurations: TotalCallsAndDurationResponse<PhoneAnalyticsTypes.IncomingCallsAggregationKey> =
    generateCallsAndDurations<PhoneAnalyticsTypes.IncomingCallsAggregationKey>(locationIds, incomingCallsKeys);

  const sourceTypeCallsAndDurations: TotalCallsAndDurationResponse<PhoneAnalyticsTypes.SourceTypeAggregationKey> =
    generateCallsAndDurations<PhoneAnalyticsTypes.SourceTypeAggregationKey>(locationIds, sourceTypeKeys);

  const longDurationCalls: LongDurationCallsResponse = {
    locationIds: locationIds.map((locationId) => {
      return {
        id: locationId,
        dates: last14Days.map((date) => {
          const hourlyLongCalls = prefillZeros(Array.from({ length: 16 }, () => Math.floor(Math.random() * 2)));

          return {
            date: dayjs(date).format('YYYY-MM-DD'),
            aggregates: totalCallsKeys.map((key) => ({
              key,
              hourly: hourlyLongCalls,
              total: hourlyLongCalls.reduce((acc, value) => acc + value, 0),
            })),
          };
        }),
      };
    }),
  };

  const deviceExtensions: DeviceExtensionsResponse = {
    LocationIds: locationIds.map((locationId) => ({
      CallAnswered: generateExtensionsCalls(last14Days, extensions),
      CallPlaced: generateExtensionsCalls(last14Days, extensions),
      Id: locationId,
    })),
  };

  const hourlyInsights: HourlyInsightsResponse = {
    locationIds: locationIds.map((locationId) => ({
      id: locationId,
      aggregates: hourlyInsightsKey.map((key) => {
        const hourlyCalls = prefillZeros(Array.from({ length: 16 }, () => Math.floor(Math.random() * 50)));

        return {
          key,
          hourly: hourlyCalls,
          total: hourlyCalls.reduce((acc, value) => acc + value, 0),
        };
      }),
    })),
  };

  const callsSummary: CallsSummaryResponse = {
    answerRate: {
      abandoned: 80,
      answered: 150,
      missed: 20,
      rate: 60,
      dates: last14Days.map((date) => ({
        date: dayjs(date).format('YYYY-MM-DD'),
        daily: generateDailyCount(),
        hourly: generateHourlyCount(),
      })),
    },
    callDuration: {
      avg: 62,
      dates: last14Days.map((date) => ({
        date: dayjs(date).format('YYYY-MM-DD'),
        daily: generateDailyDuration(),
        hourly: generateHourlyDuration(),
      })),
    },
    totalCalls: {
      inbound: 250,
      outbound: 100,
      total: 350,
      dates: last14Days.map((date) => ({
        date: dayjs(date).format('YYYY-MM-DD'),
        daily: generateDailyCount(),
        hourly: generateHourlyCount(),
      })),
    },
  };

  const phoneTreesCallVolume = {
    phoneNumbers: [
      {
        id: 'pn-1',
        phoneNumber: { nationalNumber: '1234567890' },
        locationId: 'loc-1',
      },
      {
        id: 'pn-2',
        phoneNumber: { nationalNumber: '2345678901' },
        locationId: 'loc-1',
      },
      {
        id: 'pn-3',
        phoneNumber: { nationalNumber: '3456789012' },
        locationId: 'loc-2',
      },
      {},
    ],
    locationNames: {
      'loc-1': 'Main Clinic',
      'loc-2': 'Downtown Office',
      'loc-3': 'Uptown Branch',
    },
    data: [
      {
        phoneTreeId: 'pt-1',
        phoneTreeName: 'Main Menu',
        totalCalls: 150,

        dialOptions: [
          { dialOptionNumber: '1', totalCalls: 60, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: '2', totalCalls: 10, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: '3', totalCalls: 10, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: '4', totalCalls: 10, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: '5', totalCalls: 10, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: '6', totalCalls: 10, type: DialOptionType.DIAL_OPTION_NUMBER },

          { dialOptionNumber: 'extension', totalCalls: 40, type: DialOptionType.DIAL_OPTION_EXTENSION },
        ],
        officePhoneNumberIds: ['pn-1', 'pn-2'],
      },
      {
        phoneTreeId: 'pt-2',
        phoneTreeName: 'After Hours',
        totalCalls: 100,
        dialOptions: [
          { dialOptionNumber: '5', totalCalls: 30, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: '3', totalCalls: 30, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: 'fallback', totalCalls: 40, type: DialOptionType.DIAL_OPTION_FALLBACK },
        ],
        officePhoneNumberIds: ['pn-3'],
      },
      {
        phoneTreeId: 'pt-3',
        phoneTreeName: 'Holiday Menu',
        totalCalls: 10,
        dialOptions: [{ dialOptionNumber: '1', totalCalls: 10, type: DialOptionType.DIAL_OPTION_NUMBER }],
        officePhoneNumberIds: ['pn-3'],
      },
      {
        phoneTreeId: 'pt-4',
        phoneTreeName: 'Schedule',
        totalCalls: 50,
        dialOptions: [],
        officePhoneNumberIds: ['pn-2'],
      },
      {
        phoneTreeId: 'pt-5',
        phoneTreeName: 'Customer Support',
        totalCalls: 40,
        dialOptions: [
          { dialOptionNumber: '2', totalCalls: 20, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: '3', totalCalls: 10, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: 'abandoned', totalCalls: 10, type: DialOptionType.DIAL_OPTION_ABANDONED },
        ],
        officePhoneNumberIds: ['pn-1', 'pn-2', 'pn-3'],
      },
      {
        phoneTreeId: 'pt-6',
        phoneTreeName: 'Billing',
        totalCalls: 50,
        dialOptions: [
          { dialOptionNumber: '1', totalCalls: 30, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: '2', totalCalls: 10, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: 'fallback', totalCalls: 10, type: DialOptionType.DIAL_OPTION_FALLBACK },
        ],
        officePhoneNumberIds: ['pn-2'],
      },
      {
        phoneTreeId: 'pt-7',
        phoneTreeName: 'Technical Support',
        totalCalls: 80,
        dialOptions: [
          { dialOptionNumber: '1', totalCalls: 60, type: DialOptionType.DIAL_OPTION_NUMBER },
          { dialOptionNumber: '2', totalCalls: 20, type: DialOptionType.DIAL_OPTION_NUMBER },
        ],
        officePhoneNumberIds: ['pn-1'],
      },
      {
        phoneTreeId: 'pt-8',
        phoneTreeName: 'General Inquiries',
        totalCalls: 10,
        dialOptions: [],
        officePhoneNumberIds: ['pn-2'],
      },
    ],
  };

  return {
    callAnswerRate,
    callsSummary,
    deviceExtensions,
    hourlyInsights,
    incomingCallsAndDurations,
    longDurationCalls,
    sourceTypeCallsAndDurations,
    totalCallsAndDurations,
    phoneTreesCallVolume,
  };
};
